import dayjs, {Dayjs} from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {CalendarEventModel, CalendarUnavailabilityModel, ServiceRequestAttendanceStatus, ServiceRequestModel, ServiceRequestStatus} from '@/typings/models/ServiceRequest';
import {RecurrenceScope} from '@/typings/models/Recurrence';
import {UserAvailability} from '@/typings/models/User';
import isEmpty from 'lodash/isEmpty';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {getIntl} from '@@/plugin-locale/localeExports';
import moment from 'moment-timezone';
import {prefersDarkText} from '@/utils/functions';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
export const CALENDAR_START_ACCESSOR = 'utc_start_time';
export const CALENDAR_END_ACCESSOR = 'utc_end_time';
export const getWeekdayMapping = () => {
  const firstDayOfTheWeek = dayjs.localeData().firstDayOfWeek();
  // `weekday()` is 0..6 counted from the locale's first weekday; keep the key
  // non-negative (a plain `%` gave Sunday the key -1 in Monday-first locales).
  return Object.fromEntries(
    DAYS.map((day, index) => [(index - firstDayOfTheWeek + 7) % 7, day]),
  );
};

/**
 * Whether a stored availability date ("YYYY-MM-DD") is the calendar day of
 * `moment` in the timezone `moment` is pinned to.
 *
 * `extra_days` / single-day `off_days` dates are wall-clock labels in the
 * BRANCH timezone (like the "HH:mm-HH:mm" hours next to them), so they have to
 * be compared against the branch-local date of the slot. The previous
 * `dayjs(date).isSame(slot, 'day')` built the day bounds in the BROWSER
 * timezone instead: for a branch in another timezone the additional
 * availability (or the block) then vanished from, or leaked onto, the wrong
 * day whenever the branch-local and browser-local dates disagreed.
 */
export const isOnLocalDate = (date: string | undefined | null, moment: Dayjs): boolean =>
  !!date && String(date).slice(0, 10) === moment.format('YYYY-MM-DD');

export const isWithinHours = (start: Dayjs, end: Dayjs, timeRange: string) => {
  const [startTime, endTime] = timeRange.split('-');

  const rangeStartHour = parseInt(startTime.split(':')[0]);
  const rangeStartMinute = parseInt(startTime.split(':')[1]);

  const rangeEndHour = parseInt(endTime.split(':')[0]);
  const rangeEndMinute = parseInt(endTime.split(':')[1]);

  const startHour = start.hour();
  const startMinute = start.minute();

  const endHour = end.hour() === 0 ? 24 : end.hour();
  const endMinute = end.minute();

  const isSameOrAfterStart = startHour > rangeStartHour || (startHour === rangeStartHour && startMinute >= rangeStartMinute);
  const isSameOrBeforeEnd = endHour < rangeEndHour || (endHour === rangeEndHour && endMinute <= rangeEndMinute);

  return isSameOrAfterStart && isSameOrBeforeEnd;
};

export const isAgentUnavailableInRange = (
  availability: UserAvailability,
  slotStart: Dayjs,
  slotEnd: Dayjs,
  timezones?: { source: string, target: string },
) => {
  if (isEmpty(availability)) return true;

  // Availability (working_hours / off_days / extra_days) is stored in the
  // branch's local time (`timezones.source`). Convert the SLOT into the branch
  // timezone and evaluate everything branch-locally, rather than converting the
  // hour ranges into the calendar timezone: a converted range that crosses
  // midnight (LA 09:00-17:00 seen from Berlin -> "18:00-02:00") can't be
  // expressed as a same-day "HH:mm-HH:mm" string, which made those branches
  // render as fully unavailable. Converting the slot also picks the weekday AND
  // the date (for extra_days / off_days, see isOnLocalDate) in branch-local
  // terms, so the spill-over past midnight lands on the right day.
  const start = timezones?.source ? slotStart.tz(timezones.source) : slotStart;
  const end = timezones?.source ? slotEnd.tz(timezones.source) : slotEnd;

  // --- 1) OFF DAYS / BLOCKS: always take precedence ---
  const hasBlockingOffDay = availability?.off_days?.some(offDay => {
    // Datetime-range format: { start: "YYYY-MM-DDTHH:mm:ssZ", end: "YYYY-MM-DDTHH:mm:ssZ" }
    // (no date field — covers multi-day periods like vacation blocks)
    if (!offDay.date && offDay.start && offDay.end) {
      const blockStart = dayjs(offDay.start);
      const blockEnd = dayjs(offDay.end);
      return start.isBefore(blockEnd) && end.isAfter(blockStart);
    }

    // Single-day format: { date: "YYYY-MM-DD", start?: "HH:mm", end?: "HH:mm" }
    if (!isOnLocalDate(offDay.date, start)) return false;

    // Treat offDay as a time range if start/end exist, otherwise full-day block
    if (offDay.start && offDay.end) {
      return isWithinHours(start, end, `${offDay.start}-${offDay.end}`);
    }

    // No start/end -> whole day blocked
    return true;
  });

  if (hasBlockingOffDay) {
    return true; // unavailable, regardless of working hours
  }

  // --- 2) Determine which availability ranges apply for this date ---
  const WEEKDAYS = getWeekdayMapping();
  const dayName = WEEKDAYS[start.weekday()];

  // If extra_days exist for this date, they override weekly working_hours
  const currentDayExtraDays = availability?.extra_days?.filter(extraDay =>
    isOnLocalDate(extraDay.date, start),
  );

  let ranges: string[] = [];

  if (currentDayExtraDays && currentDayExtraDays.length > 0) {
    ranges = ranges.concat(currentDayExtraDays.map(extraDay => `${extraDay.start}-${extraDay.end}`));
  }


  const currentDayWorkingHours = availability?.working_hours?.[dayName];

  if (Array.isArray(currentDayWorkingHours) && currentDayWorkingHours.length > 0) {
    ranges = ranges.concat(currentDayWorkingHours);
  }

  // No working hours / extra days -> unavailable
  if (!ranges.length) {
    return true;
  }

  // --- 3) Check if slot fits inside at least one availability range ---
  const isInsideAnyAvailableRange = ranges.some(range =>
    isWithinHours(start, end, range),
  );

  // if it's inside at least one range -> available (false)
  // otherwise -> unavailable (true)
  return !isInsideAnyAvailableRange;
};


type Interval = [number, number];

const toMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
};
const fromMinutes = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const strToInterval = (range: string): Interval => {
  const [start, end] = range.split('-');
  return [toMinutes(start), toMinutes(end)];
};

const intervalToStr = ([start, end]: Interval): string =>
  `${fromMinutes(start)}-${fromMinutes(end)}`;

const subtractIntervals = (bases: Interval[], blocks: Interval[]): Interval[] => {
  let result = bases;

  for (const [bs, be] of blocks) {
    const next: Interval[] = [];

    for (const [as, ae] of result) {
      if (ae <= bs || as >= be) {
        next.push([as, ae]);
        continue;
      }

      if (as < bs) next.push([as, bs]);
      if (ae > be) next.push([be, ae]);
    }

    result = next;
    if (!result.length) break;
  }

  return result.filter(([s, e]) => e > s);
};

export const getAvailableRangesForDay = (
  availability: UserAvailability,
  day: Dayjs,
): string[] => {
  if (isEmpty(availability)) return [];

  // `day` is pinned to the timezone the caller evaluates in; match the stored
  // dates on that calendar day, not on the browser-local one.
  const sameDay = (d: string) => isOnLocalDate(d, day);

  // Datetime-range off_days (no date field) cover day if range spans it
  const dayStart = day.startOf('day');
  const dayEnd = day.endOf('day');
  const hasRangeOffDay = (availability.off_days ?? []).some(
    od => !od.date && od.start && od.end && dayStart.isBefore(dayjs(od.end)) && dayEnd.isAfter(dayjs(od.start)),
  );
  if (hasRangeOffDay) return [];

  const offToday = (availability.off_days ?? []).filter(od => od.date && sameDay(od.date));

  // Full-day off if any single-day offDay has no start/end
  if (offToday.some(od => !od.start || !od.end)) {
    return [];
  }

  // Base ranges: extra_days for that date override working_hours
  const extraToday = (availability.extra_days ?? []).filter(ed => sameDay(ed.date));

  const WEEKDAYS = getWeekdayMapping();
  const dayName = WEEKDAYS[day.weekday()];

  let baseIntervals: Interval[] = [];

  if (extraToday.length) {
    baseIntervals = baseIntervals.concat(extraToday.map(ed => strToInterval(`${ed.start}-${ed.end}`)));
  }

  const working = availability.working_hours?.[dayName] ?? [];
  baseIntervals = baseIntervals.concat(working.map(strToInterval));

  if (!baseIntervals.length) return [];

  // Blocking intervals (single-day off_days with time ranges)
  const blockIntervals: Interval[] = offToday
    .filter(od => od.start && od.end)
    .map(od => strToInterval(`${od.start}-${od.end}`));

  if (!blockIntervals.length) {
    return baseIntervals.map(intervalToStr).sort((a, b) => a.localeCompare(b));
  }

  const finalIntervals = subtractIntervals(baseIntervals, blockIntervals);
  return finalIntervals.map(intervalToStr).sort((a, b) => a.localeCompare(b));
};

const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
const nextDate = (date: string) => dayjs(date, DATE_FORMAT).add(1, 'day').format(DATE_FORMAT);

/** Wall-clock "HH:mm-HH:mm" on a branch-local date -> [start, end] instants.
 * "24:00", "23:59" and "00:00" as the end all mean "until midnight". */
const rangeToInstants = (date: string, range: string, tz: string): [number, number] => {
  const [from, to] = range.split('-');
  const start = dayjs.tz(`${date} ${from}`, DATE_TIME_FORMAT, tz);
  const end = ['24:00', '23:59', '00:00'].includes(to)
    ? dayjs.tz(`${nextDate(date)} 00:00`, DATE_TIME_FORMAT, tz)
    : dayjs.tz(`${date} ${to}`, DATE_TIME_FORMAT, tz);
  return [start.valueOf(), end.valueOf()];
};

/**
 * The agent's available windows during ONE calendar day of the calendar
 * timezone, as "HH:mm-HH:mm" strings in that timezone — what the header
 * popover lists, and exactly what the grid shades for that column.
 *
 * Availability is stored branch-local, so for a branch in another timezone the
 * branch-local days overlapping the calendar day are evaluated, converted to
 * instants, clipped to the calendar day and re-labelled: LA 09:00-17:00 seen
 * from Berlin is "18:00-24:00" on Monday and "00:00-02:00" on Tuesday. With no
 * branch timezone, or the same one, the stored strings are returned untouched.
 *
 * @param day any instant on the calendar day, pinned to `calendarTimezone`
 */
export const getAvailableRangesForCalendarDay = (
  availability: UserAvailability,
  day: Dayjs,
  calendarTimezone: string,
  branchTimezone?: string | null,
): string[] => {
  const calendarDate = day.format(DATE_FORMAT);
  if (!branchTimezone || branchTimezone === calendarTimezone) {
    return getAvailableRangesForDay(availability, dayjs.tz(`${calendarDate} 12:00`, DATE_TIME_FORMAT, calendarTimezone));
  }

  const dayStart = dayjs.tz(`${calendarDate} 00:00`, DATE_TIME_FORMAT, calendarTimezone).valueOf();
  const dayEnd = dayjs.tz(`${nextDate(calendarDate)} 00:00`, DATE_TIME_FORMAT, calendarTimezone).valueOf();

  // Branch-local dates whose day overlaps the calendar day (at most two).
  const firstBranchDate = dayjs(dayStart).tz(branchTimezone).format(DATE_FORMAT);
  const lastBranchDate = dayjs(dayEnd - 1).tz(branchTimezone).format(DATE_FORMAT);
  const branchDates = [firstBranchDate];
  if (lastBranchDate !== firstBranchDate) branchDates.push(lastBranchDate);

  const intervals: Interval[] = [];
  branchDates.forEach((date) => {
    const branchDay = dayjs.tz(`${date} 12:00`, DATE_TIME_FORMAT, branchTimezone);
    getAvailableRangesForDay(availability, branchDay).forEach((range) => {
      const [start, end] = rangeToInstants(date, range, branchTimezone);
      const clippedStart = Math.max(start, dayStart);
      const clippedEnd = Math.min(end, dayEnd);
      if (clippedEnd > clippedStart) intervals.push([clippedStart, clippedEnd]);
    });
  });

  // Merge touching / overlapping windows (e.g. a range ending at branch
  // midnight followed by one starting at 00:00 the next branch day).
  intervals.sort((a, b) => a[0] - b[0]);
  const merged: Interval[] = [];
  intervals.forEach(([start, end]) => {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  });

  const label = (ts: number) => dayjs(ts).tz(calendarTimezone).format('HH:mm');
  return merged.map(([start, end]) => `${label(start)}-${end >= dayEnd ? '24:00' : label(end)}`);
};

export const UNASSIGNED = 'unassigned';

export const SERVICE_REQUEST_STATUS_COLORS = {
  [ServiceRequestStatus.confirmed]: 'green',
  [ServiceRequestStatus.tentative]: 'orange',
  [ServiceRequestStatus.cancelled_by_customer]: 'red',
  [ServiceRequestStatus.cancelled]: 'red',
  [ServiceRequestStatus.declined]: 'red',
  [ServiceRequestStatus.rescheduled]: 'volcano',
  [ServiceRequestStatus.rescheduled_by_customer]: 'volcano',
};

/** Attendance tag colours (antd presets, like SERVICE_REQUEST_STATUS_COLORS). */
export const SERVICE_REQUEST_ATTENDANCE_COLORS = {
  [ServiceRequestAttendanceStatus.show]: 'green',
  [ServiceRequestAttendanceStatus.no_show]: 'red',
};

/** Attendance icon colours on the calendar card (antd green-6 / red-5). */
export const SERVICE_REQUEST_ATTENDANCE_ICON_COLORS = {
  [ServiceRequestAttendanceStatus.show]: '#52c41a',
  [ServiceRequestAttendanceStatus.no_show]: '#ff4d4f',
};

export const getServiceRequestStateTitles = () => {
  const intl = getIntl();
  return {
    [ServiceRequestStatus.confirmed]: intl.formatMessage({
      id: 'pages.serviceRequest.state.confirmed',
      defaultMessage: 'Confirmed',
    }),
    [ServiceRequestStatus.tentative]: intl.formatMessage({
      id: 'pages.serviceRequest.state.tentative',
      defaultMessage: 'Tentative',
    }),
    [ServiceRequestStatus.cancelled_by_customer]: intl.formatMessage({
      id: 'pages.serviceRequest.state.cancelled',
      defaultMessage: 'Cancelled',
    }),
    [ServiceRequestStatus.cancelled]: intl.formatMessage({
      id: 'pages.serviceRequest.state.cancelled',
      defaultMessage: 'Cancelled',
    }),
    [ServiceRequestStatus.declined]: intl.formatMessage({
      id: 'pages.serviceRequest.state.declined',
      defaultMessage: 'Declined',
    }),
    [ServiceRequestStatus.rescheduled_by_customer]: intl.formatMessage({
      id: 'pages.serviceRequest.state.rescheduled',
      defaultMessage: 'Rescheduled',
    }),
    [ServiceRequestStatus.rescheduled]: intl.formatMessage({
      id: 'pages.serviceRequest.state.rescheduled',
      defaultMessage: 'Rescheduled',
    }),
  };
};


// Statuses in which an appointment is still "live" and its service / agent may be
// changed. Anything else (cancelled, cancelled_by_customer, declined, rescheduled,
// rescheduled_by_customer) is a historical record whose service & agent stay locked.
export const ACTIVE_SERVICE_REQUEST_STATUSES = [
  ServiceRequestStatus.tentative,
  ServiceRequestStatus.confirmed,
];

// Both variants of each terminal outcome — the *_by_customer twins must never be
// styled or filtered differently from the agent-triggered ones.
export const RESCHEDULED_SERVICE_REQUEST_STATUSES = [
  ServiceRequestStatus.rescheduled,
  ServiceRequestStatus.rescheduled_by_customer,
];

export const CANCELLED_SERVICE_REQUEST_STATUSES = [
  ServiceRequestStatus.cancelled,
  ServiceRequestStatus.cancelled_by_customer,
  ServiceRequestStatus.declined,
];

export const INACTIVE_SERVICE_REQUEST_STATUSES = [
  ...CANCELLED_SERVICE_REQUEST_STATUSES,
  ...RESCHEDULED_SERVICE_REQUEST_STATUSES,
];

// Allowlist by design: a denylist silently re-opens editing whenever a new terminal
// status is added. A missing status (custom calendar events) is treated as editable.
export const isServiceRequestStatusEditable = (serviceRequest?: ServiceRequestModel) =>
  !serviceRequest?.status ||
  ACTIVE_SERVICE_REQUEST_STATUSES.includes(serviceRequest.status);

export const processSingleServiceRequest = (serviceRequest, options: { timezone?: string } = {}) => {
  const startTime = serviceRequest.real_start_time || serviceRequest.utc_start_time;
  const endTime = serviceRequest.real_end_time || serviceRequest.utc_end_time;
  return {
    ...serviceRequest,
    real_start_time: startTime,
    real_end_time: endTime,

    [CALENDAR_START_ACCESSOR]: new Date(startTime),
    [CALENDAR_END_ACCESSOR]: new Date(endTime),

    resourceId: `${serviceRequest?.branch_service?.id}__${getServiceRequestAssigneeId(serviceRequest)}`,
    assigneeId: getServiceRequestAssigneeId(serviceRequest),
    isDraggable: ([
      ServiceRequestStatus.tentative,
      ServiceRequestStatus.confirmed,
    ].includes(serviceRequest.status)),

    isResizable: ([
      ServiceRequestStatus.tentative,
      ServiceRequestStatus.confirmed,
    ].includes(serviceRequest.status)),

    isEditable: ([
      ServiceRequestStatus.tentative,
      ServiceRequestStatus.confirmed,
    ].includes(serviceRequest.status)) && serviceRequest.branch_service_id,

    isCancellable: ([
      ServiceRequestStatus.confirmed,
    ].includes(serviceRequest.status)),
    isConfirmable: ([
      ServiceRequestStatus.tentative,
    ].includes(serviceRequest.status)),
    isDeclinable: ([
      ServiceRequestStatus.tentative,
    ].includes(serviceRequest.status)),
  };
};

export const processServiceRequestsForCalendar = (serviceRequests: ServiceRequestModel[]) =>
  serviceRequests.map(processSingleServiceRequest);

export const processSingleCalendarEvent = (calendarEvent: CalendarEventModel) => {
  const startTime =
    calendarEvent.real_start_time || calendarEvent.utc_start_time;
  const endTime = calendarEvent.real_end_time || calendarEvent.utc_end_time;
  const isExternal = calendarEvent.sync_role === 'external';
  return {
    ...calendarEvent,
    real_start_time: startTime,
    real_end_time: endTime,

    // The calendar_event_saved websocket payload serializes the resource
    // without Laravel's filter pass, so a standalone event arrives with
    // recurring: [] and recurring_id: {} — truthy garbage that would make
    // isRecurringEvent() treat it as a series until a full page reload.
    // Keep only well-formed values.
    recurring: calendarEvent.recurring?.id ? calendarEvent.recurring : null,
    recurring_id: typeof calendarEvent.recurring_id === 'string' ? calendarEvent.recurring_id : null,

    [CALENDAR_START_ACCESSOR]: new Date(startTime),
    [CALENDAR_END_ACCESSOR]: new Date(endTime),

    resourceId: `GLOBAL__${getServiceRequestAssigneeId(calendarEvent)}`,
    assigneeId: getServiceRequestAssigneeId(calendarEvent),
    isExternal,
    calendar_integration_type: calendarEvent.calendar_integration_type,
    // For external events the color is only used as a top-border accent (see externalCalendarEvent styles),
    // so use a darker gray for contrast against the light card background.
    color: isExternal ? '#8c8c8c' : calendarEvent.color,
    blocker: true,
    isDraggable: !isExternal,
    isResizable: !isExternal,
  };
};

export const processCalendarEvents = (calendarEvents: CalendarEventModel[]) => calendarEvents.map(processSingleCalendarEvent);

/**
 * Reflect a just-confirmed calendar-event delete in the Redux store, mirroring
 * what the websocket fan-out does for OTHER clients (calendar_event_deleted for
 * a single event / only_this, recurring_deleted / recurring_updated for series
 * scopes) — so the acting tab updates without waiting for its own echo.
 * Idempotent with that echo. `scope` is the `apply_to` used, if any.
 */
export const dispatchCalendarEventRemoval = (
  dispatch: (action: { type: string; payload?: any }) => void,
  calendarEvent: Partial<CalendarEventModel>,
  scope?: RecurrenceScope,
) => {
  const recurringId = (calendarEvent as any)?.recurring?.id;
  if (recurringId && scope && scope !== RecurrenceScope.ONLY_THIS) {
    dispatch({
      type: 'serviceRequest/removeRecurringCalendarEvents',
      payload: { recurringId },
    });
    // this_and_following keeps the head of the series — refetch brings it back.
    if (scope === RecurrenceScope.THIS_AND_FOLLOWING)
      dispatch({ type: 'serviceRequest/updateFetchCalendarViewAt' });
    return;
  }
  dispatch({
    type: 'serviceRequest/removeCalendarEvent',
    payload: { calendarEventId: calendarEvent?.id },
  });
};


export const getSlotAgentId = slot => {
  if (!Array.isArray(slot.resourceId)) return slot.resourceId;
  let agentId;
  slot.resourceId.forEach(resourceId => {
    if (resourceId.startsWith('GLOBAL')) {
      agentId = resourceId.split('__')[1];
    }
  });
  return agentId;
};

/** `<branchServiceId>__<agentId>`: one per column, so an agent's columns can differ in availability. */
export const getSlotColumnKey = slot => {
  if (!Array.isArray(slot.resourceId)) return slot.resourceId;
  return slot.resourceId[0];
};

export const getSlotBranchServiceId = slot => {
  if (!Array.isArray(slot.resourceId)) return slot.resourceId;
  return slot.resourceId?.[0]?.split('__')[0];
};


/** An agent appointment reduced to what the busy-slot painter needs. */
export interface AgentBusyInterval {
  /** Epoch ms of the appointment start. */
  start: number;
  /** Epoch ms of the appointment end. */
  end: number;
  /** The branch-service column the appointment is rendered in. */
  branchServiceId: string;
  /** Shown as the busy slot's hover tooltip when present. */
  title?: string;
}

/** Sentinel column id for server-reported unavailability windows — it never
 * matches a rendered column, so they paint every column of the agent. */
export const EXTERNAL_BRANCH_SERVICE_ID = '__external__';

/**
 * Append the windows from GET /service-requests/calendar-unavailability (the
 * agent's bookings on branch services OUTSIDE the current filter) to the
 * busy-interval index, keyed by agent uuid — the same shape and presentation
 * as a locally-detected cross-column booking.
 */
export function appendUnavailabilityIntervals(
  map: Map<string, AgentBusyInterval[]>,
  unavailabilities: CalendarUnavailabilityModel[] = [],
): Map<string, AgentBusyInterval[]> {
  unavailabilities.forEach((unavailability) => {
    const agentId = unavailability?.agent;
    if (!agentId || !unavailability.utc_start_time || !unavailability.utc_end_time) return;
    if (!map.has(agentId)) map.set(agentId, []);
    map.get(agentId)!.push({
      start: new Date(unavailability.utc_start_time).getTime(),
      end: new Date(unavailability.utc_end_time).getTime(),
      branchServiceId: EXTERNAL_BRANCH_SERVICE_ID,
      title: unavailability.title || undefined,
    });
  });
  return map;
}

/**
 * Whether a calendar slot should be shown as "busy" (taken) for an agent: true
 * when one of the agent's appointments in a DIFFERENT branch-service column
 * covers the slot's midpoint.
 *
 * - Testing the midpoint (`slotMidTs`), not any overlap, means an appointment
 *   whose edge falls inside a slot doesn't paint that whole slot — the busy
 *   highlight then lines up with the appointment's own card instead of
 *   overshooting it by a slot.
 * - Excluding the current column (same branch-service) avoids re-painting the
 *   slot the appointment's own card already occupies.
 */
export function isSlotBusyForAgent(
  slotMidTs: number,
  agentEvents: AgentBusyInterval[],
  currentBranchServiceId?: string,
): boolean {
  return !!getBusySlotInterval(slotMidTs, agentEvents, currentBranchServiceId);
}

/** The interval that makes a slot busy (see isSlotBusyForAgent), if any —
 * its title, when present, becomes the slot's hover tooltip. */
export function getBusySlotInterval(
  slotMidTs: number,
  agentEvents: AgentBusyInterval[],
  currentBranchServiceId?: string,
): AgentBusyInterval | undefined {
  return agentEvents.find(
    ({ start, end, branchServiceId }) =>
      slotMidTs >= start && slotMidTs < end && branchServiceId !== currentBranchServiceId,
  );
}

/**
 * Availability of one calendar slot for one agent column, memoized in `cache`.
 *
 * The branch timezone is part of the cache key on purpose: the same agent is
 * rendered once per branch-service column, and their working hours are
 * interpreted in each branch's own timezone before being compared against the
 * calendar's selected timezone. Keying by agent + slot alone would let
 * whichever column renders first poison the cache for the agent's columns in
 * branches with a different timezone. The same goes for a per-service custom
 * schedule, hence the column key.
 */
export const resolveSlotUnavailability = (
  cache: Map<string, boolean>,
  params: {
    agentId: string;
    /** The column the slot is in (see getSlotColumnKey); defaults to the agent. */
    columnKey?: string;
    slotStartDate: Date;
    slotDurationMinutes: number;
    availability: UserAvailability;
    branchTimezone?: string;
    calendarTimezone: string;
  },
): boolean => {
  const {agentId, columnKey, slotStartDate, slotDurationMinutes, availability, branchTimezone, calendarTimezone} = params;

  const cacheKey = `${columnKey ?? agentId}__${slotStartDate.getTime()}__${branchTimezone || ''}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey)!;

  const slotStart = dayjs(slotStartDate).tz(calendarTimezone);
  const slotEnd = slotStart.add(slotDurationMinutes, 'minutes');
  const unavailable = isAgentUnavailableInRange(availability, slotStart, slotEnd, {
    source: branchTimezone,
    target: calendarTimezone,
  });
  cache.set(cacheKey, unavailable);
  return unavailable;
};


export const getServiceRequestAssigneeId = serviceRequest => serviceRequest?.agent?.id || serviceRequest?.assigneeId || serviceRequest?.assignee?.id || UNASSIGNED;
// serviceRequest.assignee is for the calendar events | serviceRequest.agent is for the appointments.
// There is deliberately no fallback to the lead's assignee: an appointment without its own
// agent is unassigned, never implicitly owned by whoever owns the project.
export const getServiceRequestAssignee = serviceRequest => serviceRequest?.assignee || serviceRequest?.agent || {};

/**
 * The id to send as `assignee` when saving an appointment / calendar event whose
 * agent the user did not deliberately change (e.g. a plain time drag).
 *
 * Unlike getServiceRequestAssigneeId — which drives the calendar lanes and falls
 * back to the lead's assignee — this reads the event's OWN assignee only: sending
 * the lead fallback would silently assign an unassigned appointment on a drag.
 * Nothing assigned resolves to `null` (never the synthetic UNASSIGNED lane id), so
 * the payload states "no assignee" explicitly instead of omitting the key.
 */
export const getOwnAssigneeId = event =>
  event?.assignee?.id ?? event?.agent?.id ?? null;

/**
 * Attaches the assignee to a drag & drop payload (in place).
 *
 * The reschedule / calendar-event edit endpoints re-resolve the agent when the key
 * is missing, so a plain time drag would drop the current assignment — the key is
 * always sent. A drop onto a different agent lane has already written the target
 * agent onto the payload; that always wins.
 */
export const applyDragAssignee = (payload, event) => {
  if ('assignee' in payload) return payload;
  payload.assignee = getOwnAssigneeId(event);
  return payload;
};

/**
 * Whether an event overlaps the hours the calendar shows: from minHour:00 through the
 * whole maxHour hour (the grid is built to maxHour:59:59). Midnight counts as 24:00 of
 * the day it closes, otherwise an appointment ending at midnight reads as hour 0 and is
 * hidden, and one starting inside the last visible hour is cut off by an exclusive maxHour.
 */
export const isInVisibleHours = (
  startTime,
  endTime,
  { minHour, maxHour, timezone: tz }: { minHour: number; maxHour: number; timezone: string },
) => {
  const start = dayjs(startTime).tz(tz);
  const end = dayjs(endTime).tz(tz);
  const startHour = start.hour() + start.minute() / 60;
  const endHour = end.hour() + end.minute() / 60 || 24;

  return startHour < maxHour + 1 && endHour > minHour;
};

/**
 * What a drop onto a calendar column means, whatever the grouping mode. The first id of
 * the column is the one that describes it:
 *  - `<branchServiceId>__<agentId>`  branch service × agent
 *  - `<branchServiceId>`             branch service only (agents hidden)
 *  - `GLOBAL__<agentId>`             agent only
 *  - `ME__<agentId>`                 the current user's own column
 * A column that names no agent (or no service) leaves that part of the event as it is.
 *
 * In agent-only grouping the column's `<branchServiceId>__<agentId>` id carries just the first
 * service the agent works on — it says nothing about the service the user dropped on — so the
 * caller passes `agentColumns` and only the agent is read.
 */
export const parseDropColumn = (
  resourceId,
  { agentColumns = false }: { agentColumns?: boolean } = {},
): { branchServiceId?: string; agentId?: string } => {
  const columnId = Array.isArray(resourceId) ? resourceId[0] : resourceId;
  if (typeof columnId !== 'string' || !columnId) return {};
  const [head, agentId] = columnId.split('__');
  if (agentId === undefined) return { branchServiceId: head };
  if (head === 'GLOBAL' || head === 'ME' || agentColumns) return { agentId };
  return { branchServiceId: head, agentId };
};

/**
 * Attaches the dropped lane's branch service to an appointment drag payload (in place).
 *
 * Always sent, like the assignee: without it a reschedule rebooks into the calendar
 * stored on the original booking, which an earlier in-place edit (a same-time lane
 * move, the drawer's service select) does not update — so the appointment would jump
 * back to its old service. Calendar events keep their own handling.
 */
export const applyDragBranchService = (payload, event, droppedBranchServiceId?: string) => {
  if (event?.blocker || 'branch_service' in payload) return payload;
  const branchServiceId = droppedBranchServiceId || event?.branch_service_id;
  if (branchServiceId) payload.branch_service = branchServiceId;
  return payload;
};

/**
 * Request pieces for an inline start/end edit from the details drawer. Both
 * kinds take a partial PATCH (like a calendar resize), so the appointment
 * keeps its assignee and other fields; blockers additionally carry the
 * timezone the times were entered in — the backend renders notification
 * emails in it — and the recurrence scope.
 */
export const buildAppointmentTimePayload = (appointment, { start, end, timezone, applyTo }: {
  start: string,
  end: string,
  timezone?: string,
  applyTo?: string,
}) => {
  if (appointment?.blocker) {
    return {
      params: { calendarEventId: appointment.id },
      data: {
        start,
        end,
        timezone,
        ...(applyTo ? { apply_to: applyTo } : {}),
      },
    };
  }
  return {
    params: { serviceRequestId: appointment?.id },
    data: { start, end },
  };
};


export function createMomentInTz(tz: string, locale?: string) {
  const localize = (momentInstance: any) => {
    if (locale) {
      momentInstance.locale(locale);
    }
    return momentInstance;
  };

  // moment.tz(...args, tz) parses string inputs as wall-clock time IN `tz`.
  // The big-calendar localizer relies on this: its merge() composes
  // "MM/DD/YYYY HH:mm:ss" strings that must resolve in the calendar's
  // timezone. Parsing with moment(...args).tz(tz) instead would interpret
  // them in the browser timezone and only re-label afterwards, shifting the
  // visible hour window by (selected offset - browser offset).
  const m: any = (...args: any[]) => localize(moment.tz(...args, tz));

  Object.assign(m, moment);

  m.utc = (...args: any[]) => localize(moment.utc(...args).tz(tz));

  m.parseZone = (...args: any[]) => localize(moment.parseZone(...args).tz(tz));

  return m as typeof moment;
}

type SlotMathLocalizer = {
  getDstOffset: (start: Date, end: Date) => number;
  getMinutesFromMidnight: (start: Date) => number;
};

/**
 * With a moment.tz localizer, big-calendar's getSlotDate adds ELAPSED minutes,
 * but its DST corrections assume wall-clock minutes (and read the browser tz).
 * On a DST switch day that shifted the gutter by an hour against the events.
 */
export function applyElapsedMinuteSlotMath<L extends SlotMathLocalizer>(
  localizer: L,
  tzMoment: typeof moment,
): L {
  localizer.getDstOffset = () => 0;
  localizer.getMinutesFromMidnight = (start: Date) => {
    const date = tzMoment(start);
    return date.diff(date.clone().startOf('day'), 'minutes');
  };
  return localizer;
}

/**
 * Blocker events paint the whole card with the event color, so the text has to
 * flip to dark on light backgrounds (imported calendars love pastel greens).
 * Returned as CSS custom properties consumed by .blockerCalendarEvent.
 */
const DARK_EVENT_TEXT_COLORS = {
  '--event-text-color': 'rgba(0, 0, 0, 0.88)',
  '--event-text-color-strong': 'rgba(0, 0, 0, 0.82)',
};

const LIGHT_EVENT_TEXT_COLORS = {
  '--event-text-color': '#fff',
  '--event-text-color-strong': 'rgba(255, 255, 255, 0.9)',
};

export const getCalendarEventTextColorVariables = (color?: string | null) =>
  prefersDarkText(color) ? DARK_EVENT_TEXT_COLORS : LIGHT_EVENT_TEXT_COLORS;
